import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress, Link, InputAdornment,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { authAPI } from '../services/api';
import { brandColors, brandCss } from '../theme/appTheme';
import { authPrimaryButtonSx } from '../theme/formStyles';
import { AuthCoverLayout, authFont } from '../components/auth/AuthCoverLayout';
import { FORGOT_COVER } from '../components/auth/authCoverContent';
import { authTextFieldSx } from '../components/auth/authFieldStyles';

export const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [devResetUrl, setDevResetUrl] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setDevResetUrl('');
    setLoading(true);
    try {
      const res = await authAPI.forgotPassword(email);
      setSuccess(res.data.message);
      if (res.data.resetUrl) setDevResetUrl(res.data.resetUrl);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCoverLayout cover={FORGOT_COVER}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${brandCss.primary}14`,
            color: brandCss.primary,
          }}
        >
          <MarkEmailReadOutlinedIcon />
        </Box>
        <Box>
          <Typography
            sx={{
              ...authFont.heading,
              fontSize: '1.55rem',
              color: brandColors.textDark,
              lineHeight: 1.2,
            }}
          >
            Forgot password
          </Typography>
        </Box>
      </Box>
      <Typography
        sx={{
          ...authFont.body,
          fontSize: 14,
          color: brandColors.textMuted,
          mb: 2.75,
          lineHeight: 1.55,
        }}
      >
        Enter the email linked to your account. We&apos;ll send a secure link to reset your password.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>{error}</Alert>}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2.5 }}>
          {success}
          {devResetUrl && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" display="block" sx={{ mb: 1, opacity: 0.85 }}>
                Development mode — open reset page:
              </Typography>
              <Button
                component="a"
                href={devResetUrl}
                size="small"
                variant="outlined"
                color="inherit"
                endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                Open reset link
              </Button>
            </Box>
          )}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <TextField
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          required
          autoFocus
          placeholder="you@company.com"
          helperText="Use the same email you sign in with"
          sx={authTextFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          fullWidth
          endIcon={!loading ? <ArrowForwardRoundedIcon /> : undefined}
          sx={{ ...authPrimaryButtonSx, borderRadius: '4px' }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
        </Button>
      </Box>

      <Link
        component={RouterLink}
        to="/login"
        underline="hover"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 3,
          color: brandCss.primary,
          fontWeight: 700,
          fontFamily: authFont.family,
          fontSize: 13.5,
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} /> Back to Sign In
      </Link>
    </AuthCoverLayout>
  );
};
